import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

const ContextMenu = ({ items, position, onClose, onItemClick, theme }) => {
  const menuRef = useRef(null)
  const submenuRef = useRef(null)
  const mountTimeRef = useRef(Date.now())

  const [adjustedPosition, setAdjustedPosition] = useState(position)
  const [focusIndex, setFocusIndex] = useState(-1) // root menu focus
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState(null)
  const [submenuFocusIndex, setSubmenuFocusIndex] = useState(-1)

  // Root focusable indices (skip separators / headers / disabled)
  const focusable = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !item.type && !item.disabled)
    .map(({ idx }) => idx)

  // Submenu items & focusable indices (one level)
  const submenuItems = openSubmenuIndex != null ? (items[openSubmenuIndex]?.submenu || []) : []
  const submenuFocusable = submenuItems
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !item.type && !item.disabled)
    .map(({ idx }) => idx)

  // Clamp root menu position after mount / changes
  useEffect(() => {
    if (!menuRef.current || !position) return
    const menuRect = menuRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    let { x, y } = position
    if (x + menuRect.width > viewportWidth) x = Math.max(10, viewportWidth - menuRect.width - 10)
    if (y + menuRect.height > viewportHeight) y = Math.max(10, viewportHeight - menuRect.height - 10)
    x = Math.max(10, Math.min(x, viewportWidth - menuRect.width - 10))
    y = Math.max(10, Math.min(y, viewportHeight - menuRect.height - 10))
    setAdjustedPosition({ x, y })
  }, [position, items])

  // Clamp submenu position when opened
  useEffect(() => {
    if (openSubmenuIndex == null || !submenuRef.current || !menuRef.current) return
    const parentBtn = menuRef.current.querySelector(`[data-menu-index="${openSubmenuIndex}"]`)
    if (!parentBtn) return
    const parentRect = parentBtn.getBoundingClientRect()
    const subRect = submenuRef.current.getBoundingClientRect()
    let x = parentRect.right + 4
    let y = parentRect.top
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (x + subRect.width > vw) x = Math.max(10, parentRect.left - subRect.width - 4)
    if (y + subRect.height > vh) y = Math.max(10, vh - subRect.height - 10)
    submenuRef.current.style.left = `${x}px`
    submenuRef.current.style.top = `${y}px`
  }, [openSubmenuIndex, submenuItems])

  // Outside click + Escape
  useEffect(() => {
    mountTimeRef.current = Date.now()
    // Use mouseup so the initial right-click (mousedown) cannot prematurely close the menu.
    // Only close on LEFT mouseup outside, and ignore events in the first 80ms after mount
    // to avoid race conditions with event ordering.
    const handleOutsideMouseUp = (event) => {
      if (event.button !== 0) return // only left button closes
      if (Date.now() - mountTimeRef.current < 80) return // ignore very early events
      const rootContains = menuRef.current && menuRef.current.contains(event.target)
      const subContains = submenuRef.current && submenuRef.current.contains(event.target)
      if (!rootContains && !subContains) onClose()
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (openSubmenuIndex != null) {
          setOpenSubmenuIndex(null)
          setSubmenuFocusIndex(-1)
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('mouseup', handleOutsideMouseUp)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mouseup', handleOutsideMouseUp)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose, openSubmenuIndex])

  // Move focus (root or submenu depending on open state)
  const moveFocus = useCallback((dir) => {
    if (openSubmenuIndex != null) {
      if (!submenuFocusable.length) return
      setSubmenuFocusIndex(prev => {
        if (!submenuFocusable.includes(prev)) return dir > 0 ? submenuFocusable[0] : submenuFocusable[submenuFocusable.length - 1]
        const pos = submenuFocusable.indexOf(prev)
        const next = (pos + dir + submenuFocusable.length) % submenuFocusable.length
        return submenuFocusable[next]
      })
      return
    }
    if (!focusable.length) return
    setFocusIndex(prev => {
      if (!focusable.includes(prev)) return dir > 0 ? focusable[0] : focusable[focusable.length - 1]
      const pos = focusable.indexOf(prev)
      const next = (pos + dir + focusable.length) % focusable.length
      return focusable[next]
    })
  }, [focusable, submenuFocusable, openSubmenuIndex])

  // Keyboard handling
  useEffect(() => {
    const handleKey = (e) => {
      if (!menuRef.current) return
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); moveFocus(1); break
        case 'ArrowUp': e.preventDefault(); moveFocus(-1); break
        case 'Home':
          e.preventDefault()
          if (openSubmenuIndex != null) {
            if (submenuFocusable.length) setSubmenuFocusIndex(submenuFocusable[0])
          } else if (focusable.length) setFocusIndex(focusable[0])
          break
        case 'End':
          e.preventDefault()
          if (openSubmenuIndex != null) {
            if (submenuFocusable.length) setSubmenuFocusIndex(submenuFocusable[submenuFocusable.length - 1])
          } else if (focusable.length) setFocusIndex(focusable[focusable.length - 1])
          break
        case 'ArrowRight': {
          if (openSubmenuIndex != null) break
          if (focusIndex >= 0) {
            const current = items[focusIndex]
            if (current?.submenu?.length) {
              e.preventDefault()
              setOpenSubmenuIndex(focusIndex)
              if (submenuFocusable.length) setSubmenuFocusIndex(submenuFocusable[0])
            }
          }
          break
        }
        case 'ArrowLeft': {
          if (openSubmenuIndex != null) {
            e.preventDefault()
            setOpenSubmenuIndex(null)
            setSubmenuFocusIndex(-1)
            menuRef.current.querySelector(`[data-menu-index="${openSubmenuIndex}"]`)?.focus()
          }
          break
        }
        case 'Enter':
        case ' ': {
          if (openSubmenuIndex != null) {
            if (submenuFocusIndex >= 0) {
              const subItem = submenuItems[submenuFocusIndex]
              if (subItem && !subItem.disabled && !subItem.type) {
                e.preventDefault()
                handleItemActivate(subItem)
              }
            }
            break
          }
          if (focusIndex >= 0) {
            const current = items[focusIndex]
            if (!current) break
            if (current.submenu?.length) {
              e.preventDefault()
              setOpenSubmenuIndex(focusIndex)
              if (submenuFocusable.length) setSubmenuFocusIndex(submenuFocusable[0])
              break
            }
            if (!current.disabled && !current.type) {
              e.preventDefault()
              handleItemActivate(current)
            }
          }
          break
        }
        default:
          break
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [focusIndex, submenuFocusIndex, items, submenuItems, moveFocus, openSubmenuIndex, submenuFocusable])

  const handleItemActivate = (item) => {
    onItemClick(item)
    onClose()
  }

  // Initial root focus
  useEffect(() => {
    if (focusable.length) setFocusIndex(focusable[0])
  }, [items])

  // Reset submenu focus when closing submenu
  useEffect(() => {
    if (openSubmenuIndex == null) setSubmenuFocusIndex(-1)
  }, [openSubmenuIndex])

  if (!position || !items?.length) return null

  const renderItem = (item, index, isSub = false) => {
    if (item.type === 'separator') {
      return <div key={index} role="separator" className="my-1 mx-2 border-t border-white/10" />
    }
    if (item.type === 'header') {
      return (
        <div key={index} className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-white/50 font-semibold select-none">
          {item.label}
        </div>
      )
    }

    const disabled = item.disabled
    const hasSubmenu = !!item.submenu?.length
    const rootFocused = !isSub && focusIndex === index
    const subFocused = isSub && submenuFocusIndex === index
    const focused = rootFocused || subFocused

    const className = `w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors outline-none relative ${
      disabled
        ? 'text-gray-400 cursor-not-allowed'
        : focused
          ? 'text-blue-400 bg-white/10'
          : 'text-white hover:bg-white/10 hover:text-blue-400'
    }`

    // Removed key from this object per React warning; key now applied directly to <button>
    const common = {
      role: 'menuitem',
      'aria-disabled': disabled || undefined,
      tabIndex: focused ? 0 : -1,
      className,
      onClick: () => { if (!disabled && !hasSubmenu) handleItemActivate(item) },
      disabled,
    }

    if (!isSub) {
      common['data-menu-index'] = index
      common.onMouseEnter = () => {
        if (disabled) return
        setFocusIndex(index)
        if (hasSubmenu) {
          setOpenSubmenuIndex(index)
          if (submenuFocusable.length) setSubmenuFocusIndex(submenuFocusable[0])
        } else if (openSubmenuIndex != null) {
          // Close submenu if hovering non-submenu item
          setOpenSubmenuIndex(prev => (prev === index ? prev : null))
        }
      }
    } else {
      common.onMouseEnter = () => { if (!disabled) setSubmenuFocusIndex(index) }
    }

    return (
      <button key={index} {...common}>
        {item.icon && <i className={`${item.icon} w-4 flex-shrink-0`}></i>}
        <span className="flex-1">{item.label}</span>
        {item.shortcut && <span className="text-xs text-gray-400 flex-shrink-0">{item.shortcut}</span>}
        {hasSubmenu && <span className="text-xs opacity-60">›</span>}
      </button>
    )
  }

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const baseMenuClass = `fixed z-[10000] min-w-48 glass-effect rounded-xl border shadow-2xl backdrop-blur-md ${prefersReducedMotion ? '' : 'animate-in fade-in-0 zoom-in-95 duration-150'} origin-top-left will-change-transform`;
  const borderColor = theme?.borderColor || 'border-white/20'
  const submenuZ = 'z-[10001]'

  const rootMenu = (
    <div
      ref={menuRef}
      role="menu"
      className={`${baseMenuClass} ${borderColor}`}
      style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
      data-context-menu
    >
      <div className="py-2">
        {items.map((item, i) => renderItem(item, i, false))}
      </div>
    </div>
  )
  const submenu = openSubmenuIndex != null && items[openSubmenuIndex]?.submenu?.length > 0 && (
    <div
      ref={submenuRef}
      role="menu"
      className={`${baseMenuClass} ${submenuZ} ${borderColor}`}
    >
      <div className="py-2">
        {items[openSubmenuIndex].submenu.map((subItem, i) => renderItem(subItem, i, true))}
      </div>
    </div>
  )

  return createPortal(<>{rootMenu}{submenu}</>, document.body)
}

export default ContextMenu
